import {FormProvider, useForm} from "react-hook-form";
import {AccountDetailsFormLayout} from "./layout";
import {yupResolver} from "@hookform/resolvers/yup";
import {AccountDetailsFormValues} from "./types";
import {accountDetailsFormSchema} from "./form-validation-schema";
import {Button, Col, message, Row, Space} from "antd";
import {AccountDetailsFormCountryFormField} from "./country-form-field";
import {AccountDetailsFormBankFormField} from "./bank-form-field";
import {AccountDetailsFormAccountNumberFormField} from "./account-number-field";
import {usePayoutSettingsContext} from "../context";
import {handleBankName, hasBankBranch} from "./utils";
import {useSaveBank} from "./hooks";
import {StyledServiceButtonWrapper} from "../../../../../../styles/onboarding/onboarding";
import {AccountDetailsFormBankBranchFormField} from "./bank-branch-form-field";
import {useAccountNumberVerification} from "./hooks/use-account-verification";
import {isNonEu} from "../../../../../../utils/bank";

export function AccountDetailsForm() {
  const {state, dispatch} = usePayoutSettingsContext();
  const {isSavingBank, handleSaveBank} = useSaveBank();
  const {
    isVerifyingAccountNumber,
    verifyAccountNumber,
    wasAccountNumberVerified,
    responseData,
    errorVerifyingAccountNumber,
    resetMutation,
    isMutationIdle,
  } = useAccountNumberVerification();
  const formMethods = useForm<AccountDetailsFormValues>({
    resolver: yupResolver(accountDetailsFormSchema),
    mode: "onSubmit",
    defaultValues: {
      accountNumber: state.connectBankFormValues?.accountNumber || "",
      branchCode: state.connectBankFormValues?.branchCode,
      bankCode: state.connectBankFormValues?.bankCode || "",
      countryCode: state.connectBankFormValues?.countryCode || "",
    },
  });
  const countryCodeValue = formMethods.watch("countryCode");
  const bankName = handleBankName({
    banks: state.payoutBanks.banks,
    selectedBank: formMethods.watch("bankCode"),
  });

  function onSubmit(values: AccountDetailsFormValues) {
    if (
      (isNonEu.includes(countryCodeValue) || hasBankBranch.includes(countryCodeValue)) &&
      !wasAccountNumberVerified
    ) {
      if (bankName) {
        verifyAccountNumber({
          bankCode: values.bankCode,
          countryCode: values.countryCode,
          accountNumber: values.accountNumber,
          bankName: bankName.name,
        });
      } else {
        message.error("Missing bank name");
      }
    } else {
      handleSaveBank(values);
    }
  }
  return (
    <AccountDetailsFormLayout>
      <FormProvider {...formMethods}>
        <form onSubmit={formMethods.handleSubmit(onSubmit)} autoComplete="off">
          <Space direction="vertical" size={24} style={{display: "flex"}}>
            <AccountDetailsFormCountryFormField
              isVerifyingAccountNumber={isVerifyingAccountNumber}
              isMutationIdle={isMutationIdle}
              resetMutation={resetMutation}
            />
            <AccountDetailsFormBankFormField
              isVerifyingAccountNumber={isVerifyingAccountNumber}
              isMutationIdle={isMutationIdle}
              resetMutation={resetMutation}
            />
            <AccountDetailsFormBankBranchFormField
              isVerifyingAccountNumber={isVerifyingAccountNumber}
              isMutationIdle={isMutationIdle}
              resetMutation={resetMutation}
            />
            <AccountDetailsFormAccountNumberFormField
              accountName={responseData?.name}
              isVerifyingAccountNumber={isVerifyingAccountNumber}
              resetMutation={resetMutation}
              isMutationIdle={isMutationIdle}
            />
            <Row gutter={[0, 16]}>
              <Col span={24} sm={12}>
                <StyledServiceButtonWrapper>
                  <Button
                    onClick={() => {
                      formMethods.reset();
                      dispatch({
                        type: "update-payment-settings-flow",
                        payload: {
                          currentView: "initial",
                          type: state.currentStep.type,
                        },
                      });
                    }}
                    block
                    size="small"
                    type="default"
                  >
                    Back
                  </Button>
                  {((!isNonEu.includes(countryCodeValue) ||
                    !hasBankBranch.includes(countryCodeValue)) &&
                    wasAccountNumberVerified) ||
                  (!isNonEu.includes(countryCodeValue) &&
                    !hasBankBranch.includes(countryCodeValue)) ? (
                    <Button
                      block
                      size="small"
                      type="primary"
                      htmlType="submit"
                      loading={isSavingBank}
                      disabled={
                        Object.keys(formMethods.formState.errors).length > 0 ||
                        Object.values(formMethods.watch()).length < 1
                      }
                    >
                      Save changes
                    </Button>
                  ) : (
                    <Button
                      block
                      size="small"
                      type="primary"
                      htmlType="submit"
                      loading={isVerifyingAccountNumber}
                      disabled={
                        Object.keys(formMethods.formState.errors).length > 0 ||
                        Object.values(formMethods.watch()).length < 1 ||
                        errorVerifyingAccountNumber
                      }
                    >
                      Verify details
                    </Button>
                  )}
                </StyledServiceButtonWrapper>
              </Col>
            </Row>
          </Space>
        </form>
      </FormProvider>
    </AccountDetailsFormLayout>
  );
}
